import React, { createContext, useState, ReactNode, useEffect, useContext, useCallback, useMemo } from 'react';
import { db } from '../firebase';
import { collection, query, getDocs, addDoc, updateDoc, deleteDoc, doc, where } from 'firebase/firestore';
import { useAuth } from '../hooks/useAuth';
import { useGuru } from './GuruContext';
// TIDAK menggunakan VITE_SUPER_ADMIN_EMAIL — cek super admin murni dari database
import { useMasterData } from './MasterDataContext';
import { UserPermission, NewUserPermissionData, isAkunLayanan } from '../types';

export interface UserAccessContextType {
    loading: boolean;
    isSuperAdmin: boolean;
    userPermissions: UserPermission[];
    /** Hak akses akun yang sedang login. */
    myPermission: UserPermission | null;
    /** Akun layanan (tanpa data pegawai), lihat docs/PANDUAN_AKUN_LAYANAN.md. */
    isAkunLayanan: boolean;
    isAkunLayananNonaktif: boolean;
    checkUserAccess: (email: string, menuId: string) => boolean;
    addUserPermission: (data: NewUserPermissionData) => Promise<any>;
    updateUserPermission: (id: string, data: Partial<NewUserPermissionData>) => Promise<void>;
    deleteUserPermission: (id: string) => Promise<void>;
}

const UserAccessContext = createContext<UserAccessContextType | undefined>(undefined);

export const useUserAccess = () => {
    const context = useContext(UserAccessContext);
    if (context === undefined) {
        throw new Error('useUserAccess must be used within a UserAccessProvider');
    }
    return context;
};

export const UserAccessProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { authUser } = useAuth();
    const { guruRecords } = useGuru();
    const { murobbiRecords, halaqohRecords, ekskulRecords, fetchMurobbiData, fetchHalaqohData, fetchEkskulData } = useMasterData();

    // Note: Previously eager loading master data (murobbi, halaqoh, ekskul) here caused too many reads.
    // Master data should only be fetched lazily inside the components that actually need them (like dropdowns).

    const [userPermissions, setUserPermissions] = useState<UserPermission[]>([]);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!authUser) {
            setUserPermissions([]);
            setIsSuperAdmin(false);
            setLoading(false);
            return;
        }

        // Tandai sedang memuat lagi saat akun berganti, supaya halaman tidak sempat
        // memeriksa hak akses dengan daftar kosong dan menampilkan "Akses Ditolak".
        setLoading(true);

        const initializeAccess = async () => {
            // Sync User — auto-create/enforce user_permissions entry
            // Fetch all permissions setelah sync
            const snapshot = await getDocs(query(collection(db, 'user_permissions')));
            const data = snapshot.docs.map(docSnap => {
                const raw = docSnap.data();
                return { id: docSnap.id, ...raw, allowedMenus: raw.allowedMenus || [] } as UserPermission;
            });
            setUserPermissions(data);

            const myPerm = data.find(p => p.email === authUser.email);
            setIsSuperAdmin(myPerm?.role === 'super_admin');
            setLoading(false);
        };

        initializeAccess().catch(e => {
            console.error('UserAccessContext: Gagal inisialisasi', e);
            setLoading(false);
        });
    }, [authUser]);

    const checkUserAccess = useCallback((email: string, menuId: string): boolean => {
        if (!email) return false;
        email = email.toLowerCase().trim();

        // Email dibandingkan tanpa beda huruf besar/kecil (data lama bisa tersimpan campur).
        const permission = userPermissions.find(p => (p.email || '').toLowerCase().trim() === email);

        // Super admin = role 'super_admin' di tabel user_permissions (MURNI database)
        if (permission?.role === 'super_admin') return true;

        const myGuru = guruRecords.find(g => (g.email || '').toLowerCase().trim() === email);

        if (menuId === 'pg-murobbi') {
            if (murobbiRecords.some(m => String((m as any).email || '').toLowerCase().trim() === email)) return true;
        }

        if (menuId === 'pg-tahfidz') {
            if (myGuru) {
                if (halaqohRecords.some(h => h.pengampuId === myGuru.id || h.pengampu === myGuru.nama)) return true;
            }
        }

        if (menuId === 'pg-ekskul') {
            if (myGuru) {
                if (ekskulRecords.some(e => e.pengajar === myGuru.nama)) return true;
            }
        }

        if (!permission) return false;
        return (permission.allowedMenus || []).includes(menuId);
    }, [userPermissions, guruRecords, murobbiRecords, halaqohRecords, ekskulRecords]);

    const addUserPermission = async (data: NewUserPermissionData) => {
        const cleanData = { ...data };
        if (cleanData.email) {
            cleanData.email = cleanData.email.toLowerCase().trim();
        }
        const docRef = await addDoc(collection(db, 'user_permissions'), cleanData);
        setUserPermissions(prev => [...prev, { id: docRef.id, ...cleanData } as UserPermission]);
        return docRef;
    };

    const updateUserPermission = async (id: string, data: Partial<NewUserPermissionData>) => {
        const cleanData = { ...data };
        if (cleanData.email) {
            cleanData.email = cleanData.email.toLowerCase().trim();
        }
        await updateDoc(doc(db, 'user_permissions', id), cleanData);
        setUserPermissions(prev => prev.map(p => p.id === id ? { ...p, ...cleanData } : p));
    };

    const myPermission = useMemo(
        () => userPermissions.find(p => !!authUser?.email && p.email === authUser.email.toLowerCase()) ?? null,
        [userPermissions, authUser]
    );
    const akunLayanan = isAkunLayanan(myPermission);
    const akunLayananNonaktif = akunLayanan && myPermission?.aktif === false;

    const deleteUserPermission = async (id: string) => {
        await deleteDoc(doc(db, 'user_permissions', id));
        setUserPermissions(prev => prev.filter(p => p.id !== id));
    };

    return (
        <UserAccessContext.Provider value={{
            loading, isSuperAdmin, userPermissions,
            myPermission, isAkunLayanan: akunLayanan, isAkunLayananNonaktif: akunLayananNonaktif,
            checkUserAccess, addUserPermission, updateUserPermission, deleteUserPermission
        }}>
            {children}
        </UserAccessContext.Provider>
    );
};
